import type { PersistedChecklistState } from '@tour-kit/checklists';
import {
  clearPendingWorkspaceIntent,
  isWorkspaceIntent,
  readPendingWorkspaceIntent,
  type WorkspaceIntent,
} from '@/core/components/onboarding/onboarding-intent';
import type { GetStartedTaskId } from '@/core/guidance/constants';
import {
  getGuidanceProgress,
  updateGuidanceProgress,
} from '@/core/guidance/guidance-progress-client';

export type GetStartedChecklistSlice = {
  tourkit: PersistedChecklistState;
  skipped: GetStartedTaskId[];
  personal: {
    shareDone?: boolean;
  };
  /** Onboarding intent for this workspace (filters + orders Loslegen tasks). */
  intent?: WorkspaceIntent;
  /**
   * Explicit plan/trial confirmation during create/onboarding.
   * Needed because new workspaces default to `plan: free` with null
   * subscriptionStatus — indistinguishable from “never chose a plan”.
   */
  planConfirmed?: boolean;
};

const emptyTourkit = (): PersistedChecklistState => ({
  completed: {},
  dismissed: [],
  timestamp: Date.now(),
});

export function emptyGetStartedSlice(): GetStartedChecklistSlice {
  return {
    tourkit: emptyTourkit(),
    skipped: [],
    personal: {},
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isTaskId(value: unknown): value is GetStartedTaskId {
  return typeof value === 'string' && value.length > 0;
}

export function parseGetStartedSlice(
  raw: Record<string, unknown> | undefined,
): GetStartedChecklistSlice {
  if (!raw) return emptyGetStartedSlice();

  const tourkitRaw = raw.tourkit;
  const tourkit = isRecord(tourkitRaw)
    ? {
        completed: isRecord(tourkitRaw.completed)
          ? (tourkitRaw.completed as PersistedChecklistState['completed'])
          : {},
        dismissed: Array.isArray(tourkitRaw.dismissed)
          ? tourkitRaw.dismissed.filter(
              (id): id is string => typeof id === 'string',
            )
          : [],
        timestamp:
          typeof tourkitRaw.timestamp === 'number'
            ? tourkitRaw.timestamp
            : Date.now(),
        completedAt: isRecord(tourkitRaw.completedAt)
          ? (tourkitRaw.completedAt as PersistedChecklistState['completedAt'])
          : undefined,
        notifiedComplete: Array.isArray(tourkitRaw.notifiedComplete)
          ? tourkitRaw.notifiedComplete.filter(
              (id): id is string => typeof id === 'string',
            )
          : undefined,
      }
    : emptyTourkit();

  const skipped = Array.isArray(raw.skipped)
    ? raw.skipped.filter(isTaskId)
    : [];

  const personalRaw = raw.personal;
  const personal = {
    shareDone: isRecord(personalRaw) ? Boolean(personalRaw.shareDone) : false,
  };

  const intent = isWorkspaceIntent(raw.intent) ? raw.intent : undefined;
  const planConfirmed = raw.planConfirmed === true;

  return { tourkit, skipped, personal, intent, planConfirmed };
}

type ChecklistPersistenceHandlers = {
  onSave: (state: PersistedChecklistState) => void;
  onLoad: () => Promise<PersistedChecklistState | null>;
  getSlice: () => GetStartedChecklistSlice;
  skipTask: (taskId: GetStartedTaskId) => Promise<void>;
  markShareDone: () => Promise<void>;
  hydrate: () => Promise<GetStartedChecklistSlice>;
};

/**
 * Workspace-scoped checklist persistence (user × workspace) via Guidance Progress.
 * TourKit only sees `tourkit`; skips + personal share live alongside it.
 */
export function createGetStartedPersistence(
  workspaceId: string,
): ChecklistPersistenceHandlers {
  let slice = emptyGetStartedSlice();
  let hydrated = false;
  let hydratePromise: Promise<GetStartedChecklistSlice> | null = null;
  let flushTimer: ReturnType<typeof setTimeout> | null = null;

  const scheduleFlush = () => {
    if (flushTimer) clearTimeout(flushTimer);
    flushTimer = setTimeout(() => {
      flushTimer = null;
      // Only keys this module owns. Never omit planConfirmed when known —
      // and never send a full-slice wipe of unrelated onboarding flags.
      const checklists: Record<string, unknown> = {
        tourkit: slice.tourkit,
        skipped: slice.skipped,
        personal: slice.personal,
      };
      if (slice.intent) checklists.intent = slice.intent;
      if (slice.planConfirmed) checklists.planConfirmed = true;
      void updateGuidanceProgress({
        workspaceId,
        checklists,
      }).catch(() => {
        // Best-effort; local slice remains authoritative offline.
      });
    }, 400);
  };

  const hydrate = async () => {
    if (hydrated) return slice;
    if (hydratePromise) return hydratePromise;

    hydratePromise = (async () => {
      try {
        const progress = await getGuidanceProgress(workspaceId);
        slice = parseGetStartedSlice(progress?.checklists);
      } catch {
        // keep empty slice
      }

      const pending = readPendingWorkspaceIntent();
      if (!slice.intent && pending) {
        slice = { ...slice, intent: pending };
        scheduleFlush();
      }
      // Clear pending once server or merge has intent (same-device fallback done).
      if (slice.intent) {
        clearPendingWorkspaceIntent();
      }

      hydrated = true;
      return slice;
    })();

    return hydratePromise;
  };

  return {
    getSlice: () => slice,
    hydrate,
    onSave: (state) => {
      slice = {
        ...slice,
        tourkit: state,
      };
      scheduleFlush();
    },
    onLoad: async () => {
      const loaded = await hydrate();
      return loaded.tourkit;
    },
    skipTask: async (taskId) => {
      await hydrate();
      if (!slice.skipped.includes(taskId)) {
        slice = {
          ...slice,
          skipped: [...slice.skipped, taskId],
        };
        scheduleFlush();
      }
    },
    markShareDone: async () => {
      await hydrate();
      if (!slice.personal.shareDone) {
        slice = {
          ...slice,
          personal: { ...slice.personal, shareDone: true },
        };
        scheduleFlush();
      }
    },
  };
}
